import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getFileType, isImageExtension, isFontExtension } from '@/lib/fileConstants';

const QK_FILES = ['global-knowledge-files'] as const;
const QK_STATS = ['global-knowledge-stats'] as const;

export function useGlobalKnowledge() {
  const { user } = useAuth();
  return useQuery({
    queryKey: QK_FILES,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('global_knowledge_files')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    refetchInterval: (query) => {
      const files = query.state.data;
      if (!files) return false;
      const hasPending = files.some(f => f.parsing_status === 'pending' || f.parsing_status === 'processing');
      return hasPending ? 3000 : false;
    },
  });
}

export function useGlobalKnowledgeStats() {
  const { user } = useAuth();
  return useQuery({
    queryKey: QK_STATS,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('global_knowledge_stats')
        .select('*')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useUploadGlobalFile() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      if (!user) throw new Error('Usuário não autenticado');

      const fileId = crypto.randomUUID();
      const sanitizedName = file.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${fileId}/${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('global-files')
        .upload(storagePath, file);
      if (uploadError) throw uploadError;

      const isImage = isImageExtension(file.name);
      const isFont = isFontExtension(file.name);

      const { data, error: insertError } = await supabase
        .from('global_knowledge_files')
        .insert({
          id: fileId,
          file_name: file.name,
          file_type: getFileType(file.name),
          file_size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          storage_path: storagePath,
          uploaded_by: user.id,
          parsing_status: isImage || isFont ? 'ready' : 'pending',
        })
        .select()
        .single();
      if (insertError) throw insertError;

      // Documents: dispara parsing assíncrono. Imagens e fontes já estão prontas.
      if (!isImage && !isFont) {
        supabase.functions.invoke('parse-document', {
          body: { globalFileId: fileId },
        }).then(({ error }) => {
          if (error) console.error('parse-document invoke error:', error);
          queryClient.invalidateQueries({ queryKey: QK_FILES });
          queryClient.invalidateQueries({ queryKey: QK_STATS });
        });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_FILES });
      queryClient.invalidateQueries({ queryKey: QK_STATS });
    },
  });
}

export function useDeleteGlobalFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, storagePath }: { fileId: string; storagePath: string }) => {
      const { error: storageError } = await supabase.storage
        .from('global-files')
        .remove([storagePath]);
      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('global_knowledge_files')
        .delete()
        .eq('id', fileId);
      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK_FILES });
      queryClient.invalidateQueries({ queryKey: QK_STATS });
    },
  });
}
